import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, BarChart3, TrendingUp, TrendingDown, Users, Eye, MousePointer, Reply, Target, Activity, Award, AlertCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { templateService } from '../../../services/templateService';

const PerformanceAnalytics = ({ template, onClose }) => {
  const [analytics, setAnalytics] = useState(null);
  const [generationHistory, setGenerationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    loadAnalytics();
    loadGenerationHistory();
  }, [template]);

  const loadAnalytics = async () => {
    try {
      const analyticsData = await templateService?.getTemplateAnalytics(template?.id);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadGenerationHistory = async () => {
    try {
      const history = await templateService?.getGenerationHistory(10);
      setGenerationHistory(history?.filter(h => h?.template_id === template?.id));
    } catch (error) {
      console.error('Error loading generation history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPercentage = (value) => {
    return value ? `${Math.round(value * 100)}%` : 'N/A';
  };

  // Mock performance data for visualization
  const generateMockPerformanceData = () => {
    const data = [];
    const baseUsage = analytics?.usage_count || 0;
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date?.setDate(date?.getDate() - i);
      
      data?.push({
        date: formatDate(date),
        usage: Math.max(0, Math.floor(Math.random() * (baseUsage / 10) + (baseUsage / 30))),
        opens: Math.floor(Math.random() * 80 + 10),
        clicks: Math.floor(Math.random() * 25 + 5),
        responses: Math.floor(Math.random() * 15 + 2)
      });
    }
    
    return data;
  };

  const performanceData = generateMockPerformanceData();

  const getPerformanceMetrics = () => {
    const performanceData = analytics?.performance_data || {};
    
    return {
      openRate: performanceData?.open_rate || 0.45,
      clickRate: performanceData?.click_rate || 0.18,
      responseRate: performanceData?.response_rate || 0.12,
      deliveryRate: performanceData?.delivery_rate || 0.95,
      engagementRate: performanceData?.engagement_rate || 0.23,
      conversionRate: performanceData?.conversion_rate || 0.08
    };
  };

  const metrics = getPerformanceMetrics();

  const getMetricTrend = (current, previous) => {
    if (!previous) return 'neutral';
    return current > previous ? 'up' : current < previous ? 'down' : 'neutral';
  };

  const MetricCard = ({ title, value, icon, trend = 'neutral', description = '', color = 'blue' }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-2 bg-${color}-100 rounded-lg`}>
          {icon}
        </div>
        <div className="flex items-center gap-1 text-sm">
          {trend === 'up' && <TrendingUp className="h-4 w-4 text-green-500" />}
          {trend === 'down' && <TrendingDown className="h-4 w-4 text-red-500" />}
          <span className={`${
            trend === 'up' ? 'text-green-600' : 
            trend === 'down' ? 'text-red-600' : 'text-gray-500'
          }`}>
            {trend === 'up' ? '+5.2%' : trend === 'down' ? '-2.1%' : '—'}
          </span>
        </div>
      </div>
      <div className="mb-2">
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm font-medium text-gray-700">{title}</div>
      </div>
      <div className="text-xs text-gray-500">{description}</div>
    </div>
  );

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      >
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e?.target === e?.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-gray-50 rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-white border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BarChart3 className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Performance Analytics</h2>
              <p className="text-sm text-gray-600">{template?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e?.target?.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(95vh-80px)]">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
            <MetricCard
              title="Usage Count"
              value={analytics?.usage_count || 0}
              icon={<Users className="h-5 w-5 text-blue-600" />}
              trend="up"
              description="Total times used"
              color="blue"
            />
            <MetricCard
              title="Open Rate"
              value={formatPercentage(metrics?.openRate)}
              icon={<Eye className="h-5 w-5 text-green-600" />}
              trend="up"
              description="Messages opened"
              color="green"
            />
            <MetricCard
              title="Click Rate"
              value={formatPercentage(metrics?.clickRate)}
              icon={<MousePointer className="h-5 w-5 text-purple-600" />}
              trend="down"
              description="Links clicked"
              color="purple"
            />
            <MetricCard
              title="Response Rate"
              value={formatPercentage(metrics?.responseRate)}
              icon={<Reply className="h-5 w-5 text-orange-600" />}
              trend="up"
              description="Responses received"
              color="orange"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Usage Trend Chart */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Usage Trend</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Activity className="h-4 w-4" />
                  Last 30 days
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="usage" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Performance Metrics Chart */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Performance Metrics</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Target className="h-4 w-4" />
                  Engagement rates
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={performanceData?.slice(-7)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="opens" fill="#10B981" name="Opens" />
                    <Bar dataKey="clicks" fill="#8B5CF6" name="Clicks" />
                    <Bar dataKey="responses" fill="#F59E0B" name="Responses" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Template Information */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Template Details</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Type</span>
                  <span className="text-sm font-medium capitalize">{template?.type}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Category</span>
                  <span className="text-sm font-medium">{template?.category?.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Created</span>
                  <span className="text-sm font-medium">{formatDate(template?.created_at)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Last Updated</span>
                  <span className="text-sm font-medium">{formatDate(template?.updated_at)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">AI Generated</span>
                  <span className={`text-sm font-medium ${template?.ai_generated ? 'text-purple-600' : 'text-gray-900'}`}>
                    {template?.ai_generated ? 'Yes' : 'No'}
                  </span>
                </div>
                {analytics?.last_used_at && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Last Used</span>
                    <span className="text-sm font-medium">{formatDate(analytics?.last_used_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Performance Insights */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Insights</h3>
              <div className="space-y-4">
                {/* Performance Status */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Overall Performance</span>
                  </div>
                  <p className="text-sm text-blue-700">
                    {metrics?.openRate > 0.4 ? 'Excellent' : 
                     metrics?.openRate > 0.25 ? 'Good' : 
                     metrics?.openRate > 0.15 ? 'Average' : 'Needs Improvement'}
                  </p>
                </div>

                {/* Key Recommendations */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Recommendations</h4>
                  <div className="space-y-2">
                    {metrics?.openRate < 0.3 && (
                      <div className="flex items-start gap-2 text-sm">
                        <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">Consider improving subject line to increase open rates</span>
                      </div>
                    )}
                    {metrics?.clickRate < 0.1 && (
                      <div className="flex items-start gap-2 text-sm">
                        <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">Add clearer call-to-action buttons to improve click rates</span>
                      </div>
                    )}
                    {(analytics?.usage_count || 0) > 10 && metrics?.responseRate > 0.15 && (
                      <div className="flex items-start gap-2 text-sm">
                        <Award className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">Great template! Consider creating variations</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Best Performance Times */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Usage Pattern</h4>
                  <div className="text-sm text-gray-600">
                    Most active: {template?.type === 'email' ? 'Tuesday-Thursday, 10 AM - 12 PM' : 'Business hours'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Generation History for AI Templates */}
          {template?.ai_generated && generationHistory?.length > 0 && (
            <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">AI Generation History</h3>
              <div className="space-y-4">
                {generationHistory?.map((generation) => (
                  <div key={generation?.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        Generation {generation?.id?.slice(-8)}
                      </div>
                      <div className="text-xs text-gray-600">
                        {generation?.ai_prompt}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-900">
                        {generation?.generation_time_seconds}s
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(generation?.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PerformanceAnalytics;